// ── Automation integrations (Settings → Integrations) ─────────────
// Connect the coach-side tools automations post to: Slack, Notion, Google
// (Sheets + Calendar), and Zoom (video links for booked calls — Plan 3).
// OAuth runs in a popup; tokens live server-side (coach_integrations) and
// never touch the browser — this only ever reads sanitised status. The
// automations editor's "Connect …" button deep-links here. Zoom rides the
// exact same generic row/connect/disconnect code below as every other
// provider — it's just another key in _shared/integrations-oauth.js's
// PROVIDERS map (already built in Plan 3's OAuth task), so no new code
// path was needed, only this list entry.

import { callFunction } from './api.js';

const PROVIDERS = [
  { key: 'slack', label: 'Slack', desc: 'Post messages to a channel' },
  { key: 'notion', label: 'Notion', desc: 'Create items in a database' },
  { key: 'google', label: 'Google', desc: 'Add rows to Sheets · create Calendar events' },
  { key: 'zoom', label: 'Zoom', desc: 'Generate a video link for booked calls' },
];

const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

let _msgWired = false;

export async function initAutomationIntegrations() {
  const host = document.getElementById('automation-integrations-rows');
  if (!host) return;
  host.innerHTML = '<div style="color:var(--text-muted);font-size:13px;padding:8px 0">Loading…</div>';
  let status = {};
  try {
    status = await callFunction('integration-status', null, 'GET');
  } catch {
    host.innerHTML = '<div style="color:var(--text-muted);font-size:13px;padding:8px 0">Couldn’t load integrations. The automations tables may not be live yet.</div>';
    return;
  }
  host.innerHTML = PROVIDERS.map((p) => row(p, status[p.key])).join('');
  host.querySelectorAll('[data-connect]').forEach((b) => b.addEventListener('click', () => connect(b.dataset.connect)));
  host.querySelectorAll('[data-disconnect]').forEach((b) => b.addEventListener('click', () => disconnect(b.dataset.disconnect)));

  // Reload once the popup reports a successful connection.
  if (!_msgWired) {
    _msgWired = true;
    window.addEventListener('message', (e) => {
      if (e?.data?.type === 'revfit-integration-connected') initAutomationIntegrations();
    });
  }
}

function row(p, st) {
  const connected = !!st?.connected;
  const configured = st?.configured !== false;
  const statusText = connected ? `Connected${st.name ? ' · ' + esc(st.name) : ''}` : (configured ? 'Not connected' : 'Unavailable');
  const btn = connected
    ? `<button class="btn-secondary btn-sm" data-disconnect="${p.key}">Disconnect</button>`
    : (configured ? `<button class="btn-primary btn-sm" data-connect="${p.key}">Connect</button>` : '');
  return `<div data-provider="${p.key}" style="display:flex;align-items:center;gap:12px;padding:12px 0;border-bottom:1px solid var(--border)">
    <div style="flex:1;min-width:0">
      <div style="font-size:13px;font-weight:600;color:var(--text)">${esc(p.label)}</div>
      <div style="font-size:12px;color:var(--text-muted)">${esc(p.desc)}</div>
    </div>
    <div style="font-size:11px;font-weight:700;color:${connected ? '#10b981' : 'var(--text-muted)'}">${esc(statusText)}</div>
    ${btn}
  </div>`;
}

async function connect(provider) {
  try {
    const { url } = await callFunction('integration-oauth-start', { provider }, 'POST');
    window.open(url, 'revfit-oauth', 'width=600,height=760');
  } catch (e) {
    alert('Could not start the connection: ' + (e?.message || 'unknown error'));
  }
}

async function disconnect(provider) {
  if (!confirm(`Disconnect ${provider}? Automations that post to it will stop until you reconnect.`)) return;
  try {
    await callFunction('integration-disconnect', { provider }, 'POST');
    initAutomationIntegrations();
  } catch {
    alert('Could not disconnect. Try again.');
  }
}
